/*
 * Copyright (c) Nexign, JSC, 2019-2025.
 */
/**
 * Auth module selectors
 *
 * @author Aleksandr Perkhunov
 * @author Evgenij Tyan
 * @author Yakov Gushchin
 * @author Anton Panasenko
 * @author Evgeniy Naumov
 * @author Andrey Dubov
 * @author Alexander Golshtein
 * @author Ilya Baranchikov
 * @author Nikolay Sviridov
 * @version 1.38.0
 * @since 1.0.0
 */

import { createSelector, OutputSelector } from 'reselect';

import { IRootReducer } from '@/main/reducers/rootReducer';
import { FeatureNameEnum } from '@modules/role/constants/guard/guardConstants';

import { AuthorityRoleEnum, EAccountType, EUserType } from '../constants/authConstants';
import {
    IAuthData,
    IAuthExpenses,
    IAuthFileUploadLimits,
    IAuthMenu,
    IAuthority,
    IAuthState,
    IAuthSurvey,
    IAuthUser,
    IDChat,
    IUserFeature,
} from '../types/authTypes';

export const getAuthState = (state: IRootReducer): IAuthState => state.auth?.authData ?? {};
export const getAuthDataState = (state: IRootReducer) => getAuthState(state).data || {};
export const getAuthorities = (state: IRootReducer) => getAuthDataState(state).authorities || [];
export const getUserDataState = (state: IRootReducer) => getAuthDataState(state).user || {};

/*
 * -------------------------------------------
 *                AUTH STATE
 * -------------------------------------------
 */

export const makeSelectAuthData = (): OutputSelector<IRootReducer, IAuthData, (state: IAuthState) => IAuthData> =>
    createSelector(getAuthState, (state: IAuthState) => state.data);

export const makeSelectLoggedIn = (): OutputSelector<IRootReducer, boolean, (state: IAuthState) => boolean> =>
    createSelector(getAuthState, (state: IAuthState) => state.loggedIn);

export const makeSelectServiceAccessLoggedIn = (): OutputSelector<
    IRootReducer,
    boolean,
    (state: IAuthState) => boolean
> => createSelector(getAuthState, (state: IAuthState) => state.isServiceAccessLoggedIn);

export const makeSelectNewsServiceAccessLoggedIn = (): OutputSelector<
    IRootReducer,
    boolean,
    (state: IAuthState) => boolean
> => createSelector(getAuthState, (state: IAuthState) => state.isNewsServiceAccessLoggedIn);

export const makeSelectAuthIsLoading = (): OutputSelector<IRootReducer, boolean, (state: IAuthState) => boolean> =>
    createSelector(getAuthState, (state: IAuthState) => state.isLoading);

export const makeSelectAuthIsForbidden = () =>
    createSelector<IRootReducer, IAuthState, boolean>(getAuthState, (state) => state.isForbidden);

/*
 * -------------------------------------------
 *            USER AUTH STATE
 * -------------------------------------------
 */

export const makeSelectAuthUserData = (): OutputSelector<IRootReducer, IAuthUser, (state: IAuthUser) => IAuthUser> =>
    createSelector(getUserDataState, (state: IAuthUser) => state);

export const makeSelectIsSingleAccount = (): OutputSelector<IRootReducer, boolean, (state: IAuthUser) => boolean> =>
    createSelector(getUserDataState, (state: IAuthUser) => state.isSingleAccount);

export const makeSelectUserIsChatAvailable = (): OutputSelector<
    IRootReducer,
    boolean | undefined,
    (state: IAuthUser) => boolean | undefined
> => createSelector(getUserDataState, (state: IAuthUser) => state.isChatAvailable);

export const makeSelectUserIsCoordinator = (): OutputSelector<IRootReducer, boolean, (state: IAuthUser) => boolean> =>
    createSelector(getUserDataState, (state: IAuthUser) => state.isCoordinator);

export const makeSelectIsSubscriber = (): OutputSelector<IRootReducer, boolean, (state: IAuthUser) => boolean> =>
    createSelector(getUserDataState, (state: IAuthUser) => state.isSubscriber);

export const makeSelectSubscriberType = (): OutputSelector<
    IRootReducer,
    string | null,
    (state: IAuthUser) => string | null
> => createSelector(getUserDataState, (state: IAuthUser) => state.subscriberType);

export const makeSelectorAccountId = (): OutputSelector<IRootReducer, number, (state: IAuthUser) => number> =>
    createSelector(getUserDataState, (state: IAuthUser) => state.accountId);

export const makeSelectorBisId = (): OutputSelector<IRootReducer, number, (state: IAuthUser) => number> =>
    createSelector(getUserDataState, (state: IAuthUser) => state.bisId);

export const makeSelectorAuthSusrId = (): OutputSelector<IRootReducer, number, (state: IAuthUser) => number> =>
    createSelector(getUserDataState, (state: IAuthUser) => state.susrId);

export const makeSelectUsername = (): OutputSelector<IRootReducer, string, (state: IAuthUser) => string> =>
    createSelector(getUserDataState, (state: IAuthUser) => state.username);

export const makeSelectRedirectAllowed = (): OutputSelector<IRootReducer, boolean, (state: IAuthUser) => boolean> =>
    createSelector(getUserDataState, (state: IAuthUser) => state.redirectAllowed);

export const makeSelectUserIsRedirected = (): OutputSelector<IRootReducer, boolean, (state: IAuthUser) => boolean> =>
    createSelector(getUserDataState, (state: IAuthUser) => state.redirected);

export const makeSelectAuthoritiesRoles = (): OutputSelector<
    IRootReducer,
    AuthorityRoleEnum[],
    (state: IAuthState) => AuthorityRoleEnum[]
> =>
    createSelector(getAuthState, (state: IAuthState) =>
        state.data.authorities?.map((authority: IAuthority) => authority.authority),
    );

export const makeSelectAuthoritiesIsCustomUser = () =>
    createSelector<IRootReducer, IAuthority[], boolean>(
        getAuthorities,
        (state) =>
            !!state.find((authority: IAuthority) => authority.authority === AuthorityRoleEnum.ROLE_CUSTOM_USER),
    );

export const makeSelectAuthoritiesIsAssistant = (): OutputSelector<
    IRootReducer,
    boolean,
    (state: IAuthority[]) => boolean
> =>
    createSelector(getAuthorities, (state) => {
        const hasCoordinator = !!state.find(
            (authority: IAuthority) => authority.authority === AuthorityRoleEnum.ROLE_COORDINATOR_USER,
        );
        const hasAssistant = !!state.find(
            (authority: IAuthority) => authority.authority === AuthorityRoleEnum.ROLE_ASSISTANT_USER,
        );
        return !hasCoordinator && hasAssistant;
    });

export const makeSelectAuthoritiesIsCoordinator = () =>
    createSelector<IRootReducer, IAuthority[], boolean>(
        getAuthorities,
        (state) =>
            !!state.find((authority: IAuthority) => authority.authority === AuthorityRoleEnum.ROLE_COORDINATOR_USER),
    );

// Управление номером
export const makeSelectAuthoritiesIsSubsManager = () =>
    createSelector<IRootReducer, IAuthority[], boolean>(
        getAuthorities,
        (state) =>
            !!state.find((authority: IAuthority) => authority.authority === AuthorityRoleEnum.ROLE_SUBSCRIBER_MANAGE),
    );

// Управление номером/тарифом
export const makeSelectAuthoritiesIsSubsManagerRateplan = () =>
    createSelector<IRootReducer, IAuthority[], boolean>(
        getAuthorities,
        (state) =>
            !!state.find(
                (authority: IAuthority) => authority.authority === AuthorityRoleEnum.ROLE_SUBSCRIBER_MANAGE_RATEPLAN,
            ),
    );

// Служебный пользователь
export const makeSelectAuthoritiesIsServiceUser = () =>
    createSelector<IRootReducer, IAuthority[], boolean>(
        getAuthorities,
        (state) => !!state.find((authority: IAuthority) => authority.authority === AuthorityRoleEnum.ROLE_SERVICE_USER),
    );

export const makeSelectAuthoritiesIsNewsServiceUser = () =>
    createSelector<IRootReducer, IAuthority[], boolean>(
        getAuthorities,
        (state) =>
            !!state.find((authority: IAuthority) => authority.authority === AuthorityRoleEnum.ROLE_NEWS_SERVICE_USER),
    );

export const makeSelectIsDashboardAvailable = (): OutputSelector<
    IRootReducer,
    boolean,
    (state: IAuthUser) => boolean
> => createSelector(getUserDataState, (state: IAuthUser) => state.isDashboardEnabled ?? false);

//TODO: https://jira.billing.ru/browse/BBPORTAL-11083
export const makeSelectIsFeatureWithVersionEnabled = (
    featureName: FeatureNameEnum,
    featureVersion: number | number[],
    includeAllVersionsHigher = false,
): OutputSelector<IRootReducer, boolean, (state: IAuthData) => boolean> =>
    createSelector(getAuthDataState, (state: IAuthData) => {
        const resultFeature = state.features?.find((feature) => feature.name === featureName);

        if (includeAllVersionsHigher) {
            return (
                (resultFeature?.enabled &&
                    (Array.isArray(featureVersion) ? featureVersion : [featureVersion]).some(
                        (version) => resultFeature?.version >= version,
                    )) ||
                false
            );
        }

        return (
            (resultFeature?.enabled &&
                (Array.isArray(featureVersion) ? featureVersion : [featureVersion]).includes(resultFeature?.version)) ||
            false
        );
    });

export const makeSelectAuthAccountType = (): OutputSelector<
    IRootReducer,
    EAccountType,
    (state: IAuthUser) => EAccountType
> => createSelector(getUserDataState, (state: IAuthUser) => state.accountType);

export const makeSelectAuthAuthorities = (): OutputSelector<
    IRootReducer,
    IAuthority[],
    (state: IAuthData) => IAuthority[]
> => createSelector(getAuthDataState, (state: IAuthData) => state.authorities);

export const makeSelectAuthFeatures = (): OutputSelector<
    IRootReducer,
    IUserFeature[],
    (state: IAuthData) => IUserFeature[]
> => createSelector(getAuthDataState, (state: IAuthData) => state.features);

export const makeSelectAuthMenu = (): OutputSelector<IRootReducer, IAuthMenu, (state: IAuthData) => IAuthMenu> =>
    createSelector(getAuthDataState, (state: IAuthData) => state.menu);

export const makeSelectUserTypeId = () =>
    createSelector<IRootReducer, IAuthUser, EUserType>(getUserDataState, (state) => state.userTypeId);

export const makeSelectAuthSurvey = (): OutputSelector<
    IRootReducer,
    IAuthSurvey | undefined,
    (state: IAuthData) => IAuthSurvey | undefined
> => createSelector(getAuthDataState, (state: IAuthData) => state.survey);

export const makeSelectAuthExpenses = (): OutputSelector<
    IRootReducer,
    IAuthExpenses | undefined,
    (state: IAuthData) => IAuthExpenses | undefined
> => createSelector(getAuthDataState, (state: IAuthData) => state.expenses);

export const makeSelectAuthOnboardingEnabled = (): OutputSelector<
    IRootReducer,
    boolean,
    (state: IAuthData) => boolean
> => createSelector(getAuthDataState, (state: IAuthData) => !state.onboarding?.isDisabledByUser);

export const makeSelectAuthOnboardingIsVisible = (): OutputSelector<
    IRootReducer,
    boolean,
    (state: IAuthData) => boolean
> => createSelector(getAuthDataState, (state: IAuthData) => !!state.onboarding);

export const makeSelectUserNotificationPollingInterval = (): OutputSelector<
    IRootReducer,
    number,
    (state: IAuthData) => number
> => createSelector(getAuthDataState, (state: IAuthData) => state.notifications?.pollingInterval);

export const makeSelectUserNotificationLastUserActionInterval = (): OutputSelector<
    IRootReducer,
    number,
    (state: IAuthData) => number
> => createSelector(getAuthDataState, (state: IAuthData) => state.notifications?.lastUserActionInterval);

export const makeSelectNotificationNotifierInterval = (): OutputSelector<
    IRootReducer,
    number,
    (state: IAuthData) => number
> => createSelector(getAuthDataState, (state: IAuthData) => state.systemSettings?.notifier?.notifierInterval);

export const makeSelectNotificationLastUserActionInterval = (): OutputSelector<
    IRootReducer,
    number,
    (state: IAuthData) => number
> => createSelector(getAuthDataState, (state: IAuthData) => state.systemSettings?.notifier?.lastUserActionInterval);

export const makeSelectAuthSearchSettingsMsisdnFilterMaxSize = createSelector(
    getAuthDataState,
    (state: IAuthData) => state.searchSettings?.msisdnFilterMaxLength,
);

export const makeSelectAuthSearchSettingsSearchBoxVisible = (): OutputSelector<
    IRootReducer,
    boolean | undefined,
    (state: IAuthData) => boolean | undefined
> => createSelector(getAuthDataState, (state: IAuthData) => state.searchSettings?.searchBoxVisible);

export const makeSelectAuthSystemFileUploadLimits = (): OutputSelector<
    IRootReducer,
    IAuthFileUploadLimits,
    (state: IAuthData) => IAuthFileUploadLimits
> => createSelector(getAuthDataState, (state: IAuthData) => state.systemSettings?.files);

export const makeSelectAuthDChat = (): OutputSelector<
    IRootReducer,
    IDChat | undefined,
    (state: IAuthData) => IDChat | undefined
> => createSelector(getAuthDataState, (state: IAuthData) => state.dchat);

/**
 * Лучше использовать Guard (makeSelectGuardComponentEnabled)
 * @param featureName
 */
export const makeSelectFeatureEnabled = (
    featureName: FeatureNameEnum,
): OutputSelector<IRootReducer, boolean, (state: IAuthData) => boolean> =>
    createSelector(
        getAuthDataState,
        (state: IAuthData) => state.features.find((feature) => feature.name === featureName)?.enabled || false,
    );
